import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormikWrapper,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  userUpdate,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_REASON_UNKNOWN,
  setOperationStatus,
} from '@uncinc/react-drupal-core';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { FormikHelpers } from 'formik';

import { MINIMUM_PASSWORD_CHARACTER_COUNT, ROUTE_TYPE_GUIDED_SELF_HELP } from '../../../config/constants';
import { Button, CoreLink, PasswordField } from '../../../components';
import Page from '../../../containers/Page';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import useTypedDispatch from '../../../hooks/useTypedDispatch';
import useOperationStatus from '../../../hooks/useOperationStatus';
import useToast from '../../../hooks/useToast';

import './style.scss';

const PageNursePractitionerChangePassword: React.FC = () => {
  const { t } = useTranslation();
  const { account } = useRoutes(ROUTE_TYPE_GUIDED_SELF_HELP);
  const { buildClass } = useClasses('PageNursePractitionerChangePassword');
  const dispatch = useTypedDispatch();
  const operationStatus = useOperationStatus('user', 'update');
  const toast = useToast();

  useEffect(() => {
    if (operationStatus === UI_OPERATION_STATUS_SUCCESS) {
      toast.show(t('pages.nurse_practitioner_change_password.success_message'), 'success');
    }

    if (operationStatus === UI_OPERATION_STATUS_ERROR) {
      toast.show(t('pages.nurse_practitioner_change_password.error_message'), 'error');
    }

    if (operationStatus !== UI_OPERATION_STATUS_REQUEST) {
      // Consume the operation status so the toast won't show again.
      dispatch(setOperationStatus('user', 'update', null, UI_OPERATION_STATUS_REASON_UNKNOWN));
    }
  }, [operationStatus, dispatch, t, toast]);

  const validationSchema = Yup.object({
    password: Yup
      .string()
      .required(t('pages.nurse_practitioner_change_password.fields.password.validation.required')),
    newPassword: Yup
      .string()
      .required(t('pages.nurse_practitioner_change_password.fields.new_password.validation.required'))
      .min(MINIMUM_PASSWORD_CHARACTER_COUNT, t(
        'pages.nurse_practitioner_change_password.fields.new_password.validation.minimum',
        { count: MINIMUM_PASSWORD_CHARACTER_COUNT },
      ))
      .matches(/^(?=.*\d)(?=.*[a-zA-Z]).*$/, t('pages.reset_password.fields.password.validation.invalid')),
    newPasswordConfirm: Yup
      .string()
      .oneOf([Yup.ref('newPassword'), null], t('pages.nurse_practitioner_change_password.fields.new_password_confirm.validation.no_match'))
      .required(t('pages.nurse_practitioner_change_password.fields.new_password_confirm.validation.required')),
  });

  type FormValues = Yup.InferType<typeof validationSchema>;

  const initialValues: FormValues = {
    newPassword: '',
    newPasswordConfirm: '',
    password: '',
  };

  const currentUserId = useSelector<ImmutableRootState<number>, number>(
    (state) => state.getIn(['login', 'uid']),
  );

  const onSubmit = (values: FormValues, formik: FormikHelpers<FormValues>) => {
    const { newPassword, password } = values;
    formik.setSubmitting(false);
    dispatch(userUpdate(currentUserId, {
      pass: [{
        existing: password,
        value: newPassword,
      }],
    }));
  };

  return (
    <Page className={buildClass()} title={t('pages.nurse_practitioner_change_password.title')}>
      <div className="col-md-8 offset-md-2 col-lg-6 col-lg-1 py-8 py-lg-12">
        <header className={buildClass('header')}>
          <CoreLink
            to={account.base}
            title={t('buttons.back')}
            className="icon-before icon-chevron icon-orientation-left"
          />
        </header>

        <main className={buildClass('main')}>
          <h1 className={buildClass('title')}>
            {t('pages.nurse_practitioner_change_password.title')}
          </h1>
          <div className={buildClass('form-wrapper')}>
            <FormikWrapper
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              validateOnBlur={false}
              validateOnChange={false}
            >
              <PasswordField
                title={t('pages.nurse_practitioner_change_password.fields.password.label')}
                fieldName="password"
              />

              <PasswordField
                title={t('pages.nurse_practitioner_change_password.fields.new_password.label')}
                fieldName="newPassword"
              />

              <PasswordField
                title={t('pages.nurse_practitioner_change_password.fields.new_password_confirm.label')}
                fieldName="newPasswordConfirm"
              />

              <Button
                htmlType="submit"
                buttonState={operationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
              >
                {t('pages.nurse_practitioner_change_password.submit')}
              </Button>
            </FormikWrapper>
          </div>
        </main>
      </div>
    </Page>
  );
};

export default PageNursePractitionerChangePassword;
